import { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, Paper, Chip, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, CircularProgress, Snackbar, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Checkbox,
  FormControlLabel } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import DripEntryDialog from './DripEntryDialog';
import DripLeadUploadDialog from './DripLeadUploadDialog';
import VerityChecksPanel from './VerityChecksPanel';
import {
  getDripEntries, getLeadStatuses, getDripSettings, deleteDripEntry, seedEabCadence,
  getVerityStatus, dripErrorMessage, type DripEntry, type LeadStatus, type DripSettings,
  type VerityStatus,
} from '../services/drip-api';
import { formatServerTime } from '../services/server-time';

/**
 * Drip Emails — the list of entries, the first view under the Drip Campaign tab.
 *
 * An entry emails prospective students automatically. Entries are saved as drafts or paused; the
 * seven EAB emails are switched on together from the Verity status checks panel below. Whether
 * anyone is actually emailed is decided by the Verity check (not armed, dry run, enforce) and the
 * SIMS_DISABLE_DRIP_SENDS kill switch - the banner at the top says which, in plain words.
 *
 * See admissions-playbook/docs/DRIP-CAMPAIGN-PLAN.md.
 */

const KIND = {
  trigger: { label: 'Trigger', icon: <BoltIcon sx={{ fontSize: 16 }} />, help: 'Sends when something happens to a lead' },
  interval: { label: 'Interval', icon: <ScheduleIcon sx={{ fontSize: 16 }} />, help: 'Sends on a schedule, based on days of inactivity' },
} as const;

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning'> = {
  draft: 'default', active: 'success', paused: 'warning',
};

/**
 * The type cell's label and icon, for an actionType this screen may not recognise.
 *
 * `KIND[e.actionType].label` threw on a document whose actionType was missing, and took the whole
 * tab down with it. A list must not crash on unexpected data: it shows what it has and stays up.
 */
function kindOf(actionType: string | undefined) {
  return (actionType && KIND[actionType as keyof typeof KIND])
    || { label: actionType || 'unknown', icon: null, help: '' };
}

const TEST_SEND_NOTE = (
  <> You can send a test of any entry to a member of staff — open it and use <em>Send a test to staff</em>.</>
);

/**
 * Whether anyone is being emailed, and if not, why. Fails closed: a state this screen does not know,
 * or a status that could not be loaded, never reads as sending.
 */
function SendingStateAlert({ status, error, loading }: {
  status: VerityStatus | null; error: string | null; loading: boolean;
}) {
  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Could not load whether sending is on:</strong> {error}
      </Alert>
    );
  }
  if (!status) {
    return loading ? <Alert severity="info" sx={{ mb: 2 }}>Checking whether sending is on…</Alert> : null;
  }
  const reason = (status.pauseReason || '').trim().replace(/\.+$/, '');
  switch (status.sendingState) {
    case 'not-armed':
      return (
        <Alert severity={status.killSwitch === false ? 'warning' : 'info'} sx={{ mb: 2 }}>
          <strong>Verity checks are not switched on.</strong> SIMS reads no lead data from Verity and emails no one.
          {status.killSwitch === false
            && ' SIMS_DISABLE_DRIP_SENDS is off, so every hourly run is recorded as paused until the checks are armed.'}
          {TEST_SEND_NOTE}
        </Alert>
      );
    case 'paused':
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Sending is paused{reason ? `: ${reason}` : ''}.</strong> Nobody is emailed until this is fixed.
          {TEST_SEND_NOTE}
        </Alert>
      );
    case 'dry-run':
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Rehearsal:</strong> SIMS checks each lead in Verity and records what it would do. Nobody is
          emailed and no lead is stopped.
          {status.arm?.scope === 'enforce' && status.killSwitch !== false
            && ' Armed for enforce - still rehearsing because SIMS_DISABLE_DRIP_SENDS is on.'}
          {TEST_SEND_NOTE}
        </Alert>
      );
    case 'enforcing':
      return (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>Sending:</strong> every email is checked against Verity first, no more than 15 minutes before it
          goes.
          {status.cadence && !status.cadence.active
            && ' None of the seven EAB emails is active yet, so nothing is due.'}
        </Alert>
      );
    default:
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>The Verity check reported a state this screen does not know
          ({String(status.sendingState ?? 'none')}).</strong> Treat sending as paused, and reload.
        </Alert>
      );
  }
}

/** What this entry does, in one line, without opening it. */
function audienceOf(e: DripEntry): string {
  if (e.actionType === 'interval') {
    const list = e.statuses ?? [];
    const shown = list.slice(0, 3).join(', ');
    const more = list.length > 3 ? ` +${list.length - 3} more` : '';
    return `Inactive ${e.inactiveDays} days · ${shown || 'no statuses'}${more}`;
  }
  if (e.actionType === 'trigger') {
    if (e.triggerEvent === 'lead_received') return 'When a lead is received';
    return `Status changes ${e.fromStatus ? `from ${e.fromStatus} ` : ''}to ${e.toStatus ?? '—'}`;
  }
  return '—';
}

export default function DripCampaigns() {
  const [entries, setEntries] = useState<DripEntry[]>([]);
  const [leadStatuses, setLeadStatuses] = useState<LeadStatus[]>([]);
  const [settings, setSettings] = useState<DripSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DripEntry | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<DripEntry | null>(null);
  const [confirmSeed, setConfirmSeed] = useState(false);
  const [refreshSeeded, setRefreshSeeded] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [verity, setVerity] = useState<VerityStatus | null>(null);
  const [verityError, setVerityError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // On its own, so a Verity status failure cannot take the entries list down, or the other way round.
  // A failed load clears the old status rather than keep showing it: a stale "Sending" is worse than
  // "could not load".
  const loadVerity = useCallback(async () => {
    try {
      setVerity(await getVerityStatus());
      setVerityError(null);
    } catch (err) {
      setVerity(null);
      setVerityError(dripErrorMessage(err, 'The Verity check status could not be loaded.'));
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const verityLoaded = loadVerity();
    try {
      const [list, catalog, config] = await Promise.all([
        getDripEntries(), getLeadStatuses(), getDripSettings(),
      ]);
      setEntries(list);
      setLeadStatuses(catalog.statuses ?? []);
      setSettings(config);
      setError(null);
    } catch (err) {
      // The server's own message names the real cause - a missing container reads very differently
      // from "you do not have permission", and guessing between them wastes someone's afternoon.
      setError(dripErrorMessage(err, 'Drip entries could not be loaded.'));
    } finally {
      setLoading(false);
    }
    await verityLoaded;
    setRefreshKey((k) => k + 1);
  }, [loadVerity]);

  useEffect(() => { void load(); }, [load]);

  const remove = async (entry: DripEntry) => {
    setConfirmDelete(null);
    try {
      await deleteDripEntry(entry.id);
      setToast('Entry deleted.');
      await load();
    } catch (err) {
      setToast(dripErrorMessage(err, 'The entry could not be deleted.'));
    }
  };

  const seed = async () => {
    setConfirmSeed(false);
    setSeeding(true);
    try {
      const r = await seedEabCadence(refreshSeeded);
      const parts = [`${r.created.length} added`];
      if (r.replaced?.length) parts.push(`${r.replaced.length} replaced`);
      // Skipped and rejected are reported, not hidden: "11 added" when three were skipped would
      // leave someone believing wording they never reviewed is in place.
      if (r.skipped.length) parts.push(`${r.skipped.length} already existed`);
      if (r.rejected.length) parts.push(`${r.rejected.length} refused (${r.rejected[0].reason})`);
      setToast(`EAB cadence: ${parts.join(', ')}.`);
      await load();
    } catch (err) {
      setToast(dripErrorMessage(err, 'The cadence could not be loaded.'));
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
            Drip Emails
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5 }}>
            Email a lead automatically — either when something happens to them, or after a number of
            days without a reply.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload">
          <span>
            <IconButton onClick={() => void load()} disabled={loading}
                        sx={{ color: 'var(--text-secondary)' }}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
        {/* EAB's leads reach the Admissions distribution list before Verity has them, so the team
            can start the sequence from their own file rather than waiting on an integration. */}
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => setUploadOpen(true)}
          disabled={loading || !!error}
          sx={{ color: 'var(--text-primary)', borderColor: 'var(--card-border)' }}
        >
          Upload leads
        </Button>
        <Button
          variant="outlined"
          startIcon={<PlaylistAddIcon />}
          onClick={() => setConfirmSeed(true)}
          disabled={loading || seeding || !!error}
          sx={{ color: 'var(--text-primary)', borderColor: 'var(--card-border)' }}
        >
          {seeding ? 'Loading…' : 'Load the EAB cadence'}
        </Button>
        <Button
          variant="contained"
          startIcon={<AddCircleIcon />}
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          disabled={loading || !!error}
          sx={{
            backgroundColor: 'var(--accent)',
            color: 'var(--button-text)',
            '&.Mui-disabled': {
              backgroundColor: 'var(--card-bg)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--card-border)',
            },
          }}
        >
          New drip entry
        </Button>
      </Box>

      <SendingStateAlert status={verity} error={verityError} loading={loading} />

      {/* What every entry inherits. Shown here because the template box on an entry is blank when
          it uses the standard template, which reads as "nothing attached" until you see this. */}
      {settings && (
        (settings.blockers ?? []).length > 0 ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {settings.blockers.join(' ')} Entries can still be drafted and previewed.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
              Every entry uses:
            </Typography>
            <Chip size="small" variant="outlined"
                  label={`Template ${settings.defaultTemplateId}`}
                  sx={{ color: 'var(--text-primary)', borderColor: 'var(--card-border)' }} />
            <Chip size="small" variant="outlined"
                  label={`Unsubscribe group ${settings.unsubscribeGroupId}`}
                  sx={{ color: 'var(--text-primary)', borderColor: 'var(--card-border)' }} />
            <Chip size="small" variant="outlined"
                  label={`Unassigned leads sign as ${settings.defaultRepName}`}
                  sx={{ color: 'var(--text-primary)', borderColor: 'var(--card-border)' }} />
          </Box>
        )
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <VerityChecksPanel status={verity} statusError={verityError} loading={loading}
                         refreshKey={refreshKey} onChanged={() => void load()} />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {(['trigger', 'interval'] as const).map((k) => (
          <Paper key={k} sx={{ p: 2, flex: '1 1 280px', backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--card-border)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {KIND[k].icon}
              <Typography sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>{KIND[k].label}</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>{KIND[k].help}</Typography>
          </Paper>
        ))}
      </Box>

      <TableContainer component={Paper} sx={{ backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)' }}>
        <Table size="small" aria-label="Drip campaign entries">
          <TableHead>
            <TableRow>
              {['Name', 'Type', 'Audience', 'Status', 'Last run', 'Sent (7 days)', ''].map((h, i) => (
                <TableCell key={h || `actions-${i}`} sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 5, textAlign: 'center' }}>
                  <CircularProgress size={26} />
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 5, textAlign: 'center', color: 'var(--text-secondary)' }}>
                  No drip entries yet.
                </TableCell>
              </TableRow>
            ) : (
              entries.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ color: 'var(--text-primary)' }}>{e.name}</TableCell>
                  <TableCell sx={{ color: 'var(--text-primary)' }}>{kindOf(e.actionType).label}</TableCell>
                  <TableCell sx={{ color: 'var(--text-secondary)' }}>{audienceOf(e)}</TableCell>
                  <TableCell><Chip size="small" label={e.status} color={STATUS_COLOR[e.status] ?? 'default'} /></TableCell>
                  {/* A naive UTC string from the server: read as UTC (shown as local time). */}
                  <TableCell sx={{ color: 'var(--text-secondary)' }}>
                    {formatServerTime(e.lastRunAt)}
                  </TableCell>
                  <TableCell sx={{ color: 'var(--text-primary)' }}>{e.sentLast7Days ?? 0}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" aria-label={`Edit ${e.name}`}
                                onClick={() => { setEditing(e); setDialogOpen(true); }}
                                sx={{ color: 'var(--text-secondary)' }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" aria-label={`Delete ${e.name}`}
                                onClick={() => setConfirmDelete(e)}
                                sx={{ color: 'var(--text-secondary)' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DripLeadUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onImported={(summary) => { setToast(`Leads uploaded: ${summary}`); void load(); }}
      />
      <DripEntryDialog
        open={dialogOpen}
        entry={editing}
        leadStatuses={leadStatuses}
        settings={settings}
        onClose={() => setDialogOpen(false)}
        onSaved={(message) => { setDialogOpen(false); setToast(message); void load(); }}
      />

      <Dialog open={confirmSeed} onClose={() => setConfirmSeed(false)}>
        <DialogTitle>Load the approved EAB cadence?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This adds the eleven emails from the approved playbook as <strong>drafts</strong>: days 0,
            2, 4, 7, 10, 12 and 14, then the nurture emails at days 30, 50, 70 and 90.
            <ul style={{ margin: '10px 0 0 0', paddingLeft: 20 }}>
              <li>Entries you already have are left untouched — this can be run twice safely.</li>
              <li>The four nurture emails have subjects but no wording yet; the playbook did not
                  include their copy.</li>
              <li>Veteran (VA) leads are included in the audience, as Enrollment Operations
                  asked.</li>
              <li>Emails sign off as the lead's assigned advisor, or "Admissions" when there
                  isn't one.</li>
              <li>Nothing sends. They are drafts.</li>
            </ul>
          </DialogContentText>
          {/* Off by default: replacing entries discards edits, so it has to be chosen each time. */}
          <FormControlLabel
            sx={{ mt: 1.5, alignItems: 'flex-start' }}
            control={<Checkbox checked={refreshSeeded} sx={{ pt: 0.25 }}
                               onChange={(e) => setRefreshSeeded(e.target.checked)} />}
            label={
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                <strong>Also update the entries already loaded</strong> to the current approved
                wording. Drafts created by this button only — anything active, or written by a
                person, is untouched. <strong>Edits to those drafts are lost.</strong>
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSeed(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void seed()}
                  sx={{ backgroundColor: 'var(--accent)' }}>Add the drafts</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete this entry?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{confirmDelete?.name}" will be removed. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" onClick={() => confirmDelete && void remove(confirmDelete)}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={toast ?? ''}
      />
    </Box>
  );
}
